import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { api } from "@/lib/api";
import { User } from "@/types";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import ModuleAccessModal from "@/components/ModuleAccessModal";
import Pagination from "@/components/ui/Pagination";
import {
  ShieldCheck,
  ShieldAlert,
  Search,
  Plus,
  Edit2,
  Trash2,
  CheckCircle,
  XCircle,
  X,
  Mail,
  Phone,
  Calendar,
  AlertTriangle,
  RefreshCw,
  UserCheck,
} from "lucide-react";

// Local pagination shape returned by GET /admin/admins — narrower than the
// shared PaginationMeta (which other pages rely on for totalPages/hasNextPage/hasPrevPage).
interface AdminListMeta {
  page: number;
  limit: number;
  total: number;
}

const AdminManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState<AdminListMeta>({
    page: 1,
    limit: 10,
    total: 0,
  });

  // Modals state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);
  const [permissionsAdmin, setPermissionsAdmin] = useState<User | null>(null);

  // Debounce search term changes (400ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchAdmins(currentPage, debouncedSearchTerm, roleFilter);
  }, [currentPage, debouncedSearchTerm, roleFilter]);

  const fetchAdmins = async (page = 1, search = "", role = "ALL") => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, any> = {
        page,
        limit: 10,
      };
      if (search.trim()) {
        params.search = search.trim();
      }
      if (role !== "ALL") {
        params.role = role;
      }

      const response = await api.get<{ data: User[]; meta: AdminListMeta }>(
        "/admin/admins",
        params,
      );

      if (response && Array.isArray(response.data)) {
        setAdmins(response.data);
        if (response.meta) {
          setPaginationMeta(response.meta);
        }
      } else if (Array.isArray(response)) {
        setAdmins(response as unknown as User[]);
      } else {
        setAdmins([]);
      }
    } catch (err: any) {
      console.error("Failed to fetch admin users:", err);
      setError(err.message || "Failed to fetch admin users");
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAdmins(currentPage, debouncedSearchTerm, roleFilter);
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center space-x-2">
            <ShieldCheck className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold text-gray-900">
              Admin Management
            </h1>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Manage system administrators and super admin access privileges.
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            title="Refresh Data"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>

          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Admin
          </button>
        </div>
      </div>

      {/* Filters & Search Bar */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row gap-4 justify-between items-center">
        <div className="w-full md:w-96 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search admins by name, email or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-8 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              title="Clear search"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        <div className="flex items-center space-x-3 w-full md:w-auto">
          <span className="text-sm font-medium text-gray-600">Role:</span>
          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="border border-gray-300 rounded-md text-sm px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="ALL">All Roles</option>
            <option value="admin">Admin</option>
            <option value="csm">CSM</option>
            <option value="super_admin">Super Admin</option>
          </select>
        </div>
      </div>

      {/* Error state */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between text-red-700">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
            <span>{error}</span>
          </div>
          <button
            onClick={handleRefresh}
            className="text-sm font-medium text-red-700 underline hover:text-red-800"
          >
            Try Again
          </button>
        </div>
      )}

      {/* Table Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <RefreshCw className="h-8 w-8 text-primary animate-spin mx-auto mb-3" />
            <p className="text-sm text-gray-500">Loading administrators...</p>
          </div>
        ) : admins.length === 0 ? (
          <div className="py-16 text-center">
            <UserCheck className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-lg font-medium text-gray-900">
              No Administrators Found
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {searchTerm || roleFilter !== "ALL"
                ? "No admin users matched your filter criteria."
                : "Get started by creating your first admin user."}
            </p>
            {!searchTerm && roleFilter === "ALL" && (
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Admin
              </button>
            )}
          </div>
        ) : (
          <>
          <ul className="divide-y divide-gray-200 md:hidden">
            {admins.map((admin) => {
              const isSuperAdmin =
                admin.roles?.includes("super_admin") || admin.userType === "super_admin";
              const canEdit = !isSuperAdmin || admin.id === currentUser?.id;
              return (
                <li key={admin.id} className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                      {admin.name ? admin.name.charAt(0).toUpperCase() : "A"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 truncate">
                        {admin.name || "Unnamed Admin"}
                      </p>
                      <p className="text-xs text-gray-500 font-mono">ID: {admin.id.slice(0, 8)}...</p>
                    </div>
                    {admin.isActive ? (
                      <span className="shrink-0 inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">
                        <CheckCircle className="h-3 w-3 mr-1 text-emerald-500" />
                        Active
                      </span>
                    ) : (
                      <span className="shrink-0 inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full border border-gray-200">
                        <XCircle className="h-3 w-3 mr-1 text-gray-400" />
                        Inactive
                      </span>
                    )}
                  </div>

                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-gray-600">
                    <span className="flex items-center gap-1">
                      <Phone className="h-3 w-3 text-gray-400" />
                      {admin.phoneNumber || "N/A"}
                    </span>
                    {admin.email && (
                      <span className="flex items-center gap-1 truncate">
                        <Mail className="h-3 w-3 text-gray-400 shrink-0" />
                        {admin.email}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString() : "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1.5">
                    {isSuperAdmin ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                        Super Admin
                      </span>
                    ) : (
                      <>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                          Admin
                        </span>
                        {(admin.roles?.includes("csm") || admin.roles?.includes("CSM")) && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                            CSM
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                      disabled={isSuperAdmin}
                      onClick={() => {
                        if (isSuperAdmin) return;
                        setPermissionsAdmin(admin);
                      }}
                      className={cn(
                        "p-1.5 rounded-md transition-colors",
                        isSuperAdmin
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:text-primary hover:bg-gray-100",
                      )}
                      aria-label="Manage module access"
                    >
                      <ShieldCheck className="h-4 w-4" />
                    </button>
                    <button
                      disabled={!canEdit}
                      onClick={() => {
                        if (!canEdit) return;
                        setSelectedAdmin(admin);
                        setShowEditModal(true);
                      }}
                      className={cn(
                        "p-1.5 rounded-md transition-colors",
                        !canEdit
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:text-primary hover:bg-gray-100",
                      )}
                      aria-label="Edit Admin Details"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      disabled={isSuperAdmin}
                      onClick={() => {
                        if (isSuperAdmin) return;
                        setSelectedAdmin(admin);
                        setShowDeleteModal(true);
                      }}
                      className={cn(
                        "p-1.5 rounded-md transition-colors",
                        isSuperAdmin
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:text-red-600 hover:bg-red-50",
                      )}
                      aria-label="Delete Admin"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Admin User
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Contact Details
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Role Type
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Status
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Joined Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {admins.map((admin) => {
                  const isSuperAdmin =
                    admin.roles?.includes("super_admin") || admin.userType === "super_admin";
                  return (
                    <tr
                      key={admin.id}
                      className="hover:bg-gray-50/80 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm">
                            {admin.name
                              ? admin.name.charAt(0).toUpperCase()
                              : "A"}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-semibold text-gray-900">
                              {admin.name || "Unnamed Admin"}
                            </div>
                            <div className="text-xs text-gray-500 font-mono">
                              ID: {admin.id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 flex items-center gap-1.5">
                          <Phone className="h-3.5 w-3.5 text-gray-400" />
                          <span>{admin.phoneNumber || "N/A"}</span>
                        </div>
                        {admin.email && (
                          <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                            <Mail className="h-3.5 w-3.5 text-gray-400" />
                            <span>{admin.email}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isSuperAdmin ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 border border-purple-200">
                            Super Admin
                          </span>
                        ) : (
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 border border-blue-200">
                              Admin
                            </span>
                            {(admin.roles?.includes("csm") ||
                              admin.roles?.includes("CSM")) && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 border border-amber-200">
                                CSM
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {admin.isActive ? (
                          <span className="inline-flex items-center text-xs font-medium text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                            <CheckCircle className="h-3.5 w-3.5 mr-1 text-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full border border-gray-200">
                            <XCircle className="h-3.5 w-3.5 mr-1 text-gray-400" />
                            Inactive
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                          <Calendar className="h-3.5 w-3.5 text-gray-400" />
                          {admin.createdAt
                            ? new Date(admin.createdAt).toLocaleDateString()
                            : "N/A"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {/* Super admins already hold every module, so there is
                              nothing to configure for them. */}
                          <button
                            disabled={isSuperAdmin}
                            onClick={() => {
                              if (isSuperAdmin) return;
                              setPermissionsAdmin(admin);
                            }}
                            className={cn(
                              "p-1.5 rounded-md transition-colors",
                              isSuperAdmin
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-gray-600 hover:text-primary hover:bg-gray-100",
                            )}
                            title={
                              isSuperAdmin
                                ? "Super Admins always have access to every module"
                                : "Manage module access"
                            }
                          >
                            <ShieldCheck className="h-4 w-4" />
                          </button>
                          {(() => {
                            const canEdit = !isSuperAdmin || admin.id === currentUser?.id;
                            return (
                              <button
                                disabled={!canEdit}
                                onClick={() => {
                                  if (!canEdit) return;
                                  setSelectedAdmin(admin);
                                  setShowEditModal(true);
                                }}
                                className={cn(
                                  "p-1.5 rounded-md transition-colors",
                                  !canEdit
                                    ? "text-gray-300 cursor-not-allowed"
                                    : "text-gray-600 hover:text-primary hover:bg-gray-100",
                                )}
                                title={
                                  !canEdit
                                    ? "Super Admin accounts cannot be edited by other Super Admins"
                                    : "Edit Admin Details"
                                }
                              >
                                <Edit2 className="h-4 w-4" />
                              </button>
                            );
                          })()}
                          <button
                            disabled={isSuperAdmin}
                            onClick={() => {
                              if (isSuperAdmin) return;
                              setSelectedAdmin(admin);
                              setShowDeleteModal(true);
                            }}
                            className={cn(
                              "p-1.5 rounded-md transition-colors",
                              isSuperAdmin
                                ? "text-gray-300 cursor-not-allowed"
                                : "text-gray-600 hover:text-red-600 hover:bg-red-50",
                            )}
                            title={
                              isSuperAdmin
                                ? "Super Admin accounts cannot be deleted"
                                : "Delete Admin"
                            }
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}

        {/* Pagination Footer */}
        {paginationMeta && (() => {
          const limit = paginationMeta.limit || 10;
          const totalPages = Math.ceil(paginationMeta.total / limit) || 1;
          return (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
                total={paginationMeta.total}
                pageSize={limit}
                itemLabel="admins"
              />
            </div>
          );
        })()}
      </div>

      {/* Module Access Modal */}
      {permissionsAdmin && (
        <ModuleAccessModal
          adminId={permissionsAdmin.id}
          adminName={permissionsAdmin.name || permissionsAdmin.email || "this admin"}
          onClose={() => setPermissionsAdmin(null)}
          onSaved={() => fetchAdmins(currentPage, debouncedSearchTerm, roleFilter)}
        />
      )}

      {/* Add Admin Modal */}
      {showAddModal && (
        <AddEditAdminModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchAdmins(currentPage, debouncedSearchTerm, roleFilter);
          }}
          title="Add New Admin User"
        />
      )}

      {/* Edit Admin Modal */}
      {showEditModal && selectedAdmin && (
        <AddEditAdminModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedAdmin(null);
            fetchAdmins(currentPage, debouncedSearchTerm, roleFilter);
          }}
          admin={selectedAdmin}
          title="Edit Admin User"
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedAdmin && (
        <DeleteAdminModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setSelectedAdmin(null);
          }}
          onSuccess={() => {
            setShowDeleteModal(false);
            setSelectedAdmin(null);
            fetchAdmins(currentPage, debouncedSearchTerm, roleFilter);
          }}
          admin={selectedAdmin}
        />
      )}
    </div>
  );
};

// Subcomponent: Add/Edit Admin Modal
interface AddEditAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin?: User;
  title: string;
}

const adminFormSchema = z.object({
  name: z.string().trim().min(1, "Full name is required"),
  email: z.string().trim().email("Please enter a valid email address"),
  phoneNumber: z
    .string()
    .transform((val) => val.replace(/\D/g, ""))
    .refine((val) => val.length === 10, {
      message: "Please enter a valid 10-digit phone number.",
    }),
  userType: z.enum(["admin", "super_admin"]),
  isCsm: z.boolean(),
});

type AdminFormData = z.infer<typeof adminFormSchema>;

const AddEditAdminModal: React.FC<AddEditAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  admin,
  title,
}) => {
  const { user: currentUser } = useAuth();
  const isEditingSelf = Boolean(admin && admin.id === currentUser?.id);
  const [errorMsg, setErrorMsg] = useState("");
  const [showSuperAdminConfirm, setShowSuperAdminConfirm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<AdminFormData>({
    resolver: zodResolver(adminFormSchema),
    defaultValues: {
      name: "",
      email: "",
      phoneNumber: "",
      userType: "admin",
      isCsm: false,
    },
  });

  const userType = watch("userType");

  useEffect(() => {
    if (admin) {
      const isSuper =
        admin.roles?.includes("super_admin") || admin.userType === "super_admin";
      reset({
        name: admin.name || "",
        email: admin.email || "",
        phoneNumber: admin.phoneNumber?.replace(/^\+91/, "") || "",
        userType: isSuper ? "super_admin" : "admin",
        isCsm: Boolean(admin.roles?.includes("csm")),
      });
    } else {
      reset({
        name: "",
        email: "",
        phoneNumber: "",
        userType: "admin",
        isCsm: false,
      });
    }
    setErrorMsg("");
    setShowSuperAdminConfirm(false);
  }, [admin, isOpen, reset]);

  const executeSave = async (data: AdminFormData) => {
    setErrorMsg("");

    try {
      const formattedPhone = `+91${data.phoneNumber}`;

      const payload: Record<string, any> = {
        name: data.name,
        email: data.email,
        phoneNumber: formattedPhone,
        userType: data.userType,
      };

      // Preserve any role this form doesn't manage (e.g. "student", granted
      // separately to let a super_admin log in to the student portal) —
      // otherwise saving an unrelated field here silently revokes it.
      const managedRoleKeys = ["admin", "super_admin", "csm"];
      const extraRoles = (admin?.roles || []).filter(
        (r) => !managedRoleKeys.includes(r),
      );

      if (data.userType === "admin") {
        payload.roles = Array.from(
          new Set([...(data.isCsm ? ["admin", "csm"] : ["admin"]), ...extraRoles]),
        );
      } else {
        payload.roles = Array.from(new Set(["super_admin", ...extraRoles]));
      }

      if (admin) {
        await api.patch(`/admin/admins/${admin.id}`, payload);
      } else {
        await api.post("/admin/admins", payload);
      }
      onSuccess();
    } catch (err: any) {
      console.error("Save admin error:", err);
      setErrorMsg(err.message || "Failed to save admin user details.");
    } finally {
      setShowSuperAdminConfirm(false);
    }
  };

  const onSubmit = (data: AdminFormData) => {
    const wasAlreadySuper =
      admin &&
      (admin.roles?.includes("super_admin") || admin.userType === "super_admin");

    if (data.userType === "super_admin" && !wasAlreadySuper) {
      setShowSuperAdminConfirm(true);
      return;
    }

    void executeSave(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full shadow-xl overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-lg"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          {errorMsg && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
              {errorMsg}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              {...register("name")}
              placeholder="e.g. Rahul Sharma"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:outline-none"
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              {...register("email")}
              placeholder="admin@gyanplug.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-primary focus:outline-none"
            />
            {errors.email && (
              <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex">
              <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                +91
              </span>
              <input
                type="tel"
                maxLength={10}
                {...register("phoneNumber")}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\D/g, "");
                  setValue("phoneNumber", cleaned, { shouldValidate: true });
                }}
                placeholder="9876543210"
                className="w-full px-3 py-2 border border-gray-300 rounded-r-md text-sm focus:ring-2 focus:ring-primary focus:outline-none"
              />
            </div>
            {errors.phoneNumber && (
              <p className="mt-1 text-xs text-red-600">
                {errors.phoneNumber.message}
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Role Privilege <span className="text-red-500">*</span>
            </label>
            <select
              disabled={isEditingSelf}
              {...register("userType")}
              className={cn(
                "w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-primary focus:outline-none",
                isEditingSelf &&
                  "bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200",
              )}
            >
              <option value="admin">Admin (Standard Access)</option>
              <option value="super_admin">Super Admin (Full Access)</option>
            </select>
            {isEditingSelf && (
              <p className="mt-1 text-[11px] text-gray-500">
                You cannot change your own role privilege.
              </p>
            )}

            {userType === "admin" && (
              <div className="flex items-center space-x-2 pt-2">
                <input
                  type="checkbox"
                  id="isCsmCheckbox"
                  {...register("isCsm")}
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded cursor-pointer"
                />
                <label
                  htmlFor="isCsmCheckbox"
                  className="text-xs font-semibold text-gray-700 cursor-pointer select-none"
                >
                  Is this user a CSM?
                </label>
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-100 flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 inline-flex items-center"
            >
              {isSubmitting && (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              )}
              {admin ? "Update Admin" : "Create Admin"}
            </button>
          </div>
        </form>

        {/* Super Admin Confirmation Modal */}
        {showSuperAdminConfirm && (
          <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4">
            <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-2xl space-y-4 border border-purple-100 animate-in fade-in zoom-in duration-150">
              <div className="flex items-start space-x-3">
                <div className="p-2 bg-purple-100 rounded-full text-purple-700 flex-shrink-0 mt-0.5">
                  <ShieldAlert className="h-6 w-6" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">
                    Grant Super Admin Access?
                  </h3>
                  <p className="text-xs text-gray-600 mt-1">
                    You are about to assign <span className="font-semibold text-purple-700">Super Admin</span> privileges to <span className="font-semibold text-gray-900">{getValues("name") || getValues("email")}</span>.
                  </p>
                </div>
              </div>

              <div className="bg-purple-50/70 border border-purple-200 rounded-md p-3 text-xs text-purple-900 space-y-1">
                <p className="font-semibold">This user will receive full unrestricted access to:</p>
                <ul className="list-disc list-inside space-y-0.5 text-[11px] text-purple-800">
                  <li>System-wide Admin Management (Add/Edit Admins)</li>
                  <li>Full control over platform settings & system logs</li>
                  <li>All management and administrative capabilities</li>
                </ul>
              </div>

              <div className="flex justify-end space-x-2 pt-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowSuperAdminConfirm(false)}
                  className="px-3.5 py-2 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => void executeSave(getValues())}
                  className="px-3.5 py-2 text-xs font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-md shadow-sm disabled:opacity-50 inline-flex items-center"
                >
                  {isSubmitting && (
                    <RefreshCw className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  )}
                  Confirm & Grant Super Admin
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Subcomponent: Delete Admin Modal
interface DeleteAdminModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  admin: User;
}

const DeleteAdminModal: React.FC<DeleteAdminModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  admin,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleDelete = async () => {
    setIsDeleting(true);
    setErrorMsg("");
    try {
      await api.delete(`/admin/admins/${admin.id}`);
      onSuccess();
    } catch (err: any) {
      console.error("Delete admin error:", err);
      setErrorMsg(err.message || "Failed to delete admin user.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-sm w-full p-6 shadow-xl space-y-4">
        <div className="flex items-center space-x-3 text-red-600">
          <AlertTriangle className="h-6 w-6" />
          <h3 className="text-lg font-bold text-gray-900">
            Delete Admin User?
          </h3>
        </div>

        {errorMsg && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">
            {errorMsg}
          </div>
        )}

        <p className="text-sm text-gray-600">
          Are you sure you want to delete administrator{" "}
          <span className="font-semibold text-gray-900">
            {admin.name || admin.email}
          </span>
          ? This action cannot be undone.
        </p>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={isDeleting}
            className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center"
          >
            {isDeleting && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
            Delete Admin
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement;
